/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo } from 'react';
import TableCommon from 'Components/Common/Table/TableCommon';
import { formatCurrency } from 'Utils/Numbers';
import { Cell } from 'react-table';
import AliTable, { AliColumn, AliSortType } from 'Components/Common/Table/AliTable';
import { formatDecimal, getSum } from 'Utils/Numbers';
import dayjs from 'dayjs';
import { DATE_FORMAT_11 } from 'Constant/Date';

//* TAX = 1 + 10%
const TAX = 1.1;
interface TableCostMiniProps {
  dataSource: any[];
  dataDays: any[];
  onSort?: (sortingState: AliSortType[]) => void;
}

const TableDailyReport = ({ dataSource, dataDays, onSort }: TableCostMiniProps): JSX.Element => {
  const columns = useMemo(
    () => [
      {
        Header: '',
        accessor: 'index',
        className: 'pe-none',
        columns: [
          {
            Header: '数量',
            width: '80',
            accessor: 'monthAmount',
            className: 'hidden-sort',
            Cell: ({ row }: Cell<any>) => {
              return (
                <div>
                  {row?.original?.monthAmount ? formatCurrency(row?.original?.monthAmount) : ''}
                </div>
              );
            },
          },
          {
            Header: '単価',
            width: '80',
            accessor: 'monthAverageUnitPrice',
            className: 'hidden-sort',
            Cell: ({ row }: Cell<any>) => {
              return <div>{formatCurrency(row.original.monthAverageUnitPrice || 0)}</div>;
            },
          },
          {
            Header: '金額',
            width: '80',
            accessor: 'monthTotalPrice',
            className: 'hidden-sort',
            Cell: ({ row }: Cell<any>) => {
              return <div>{formatCurrency(row.original.monthTotalPrice || 0)}</div>;
            },
          },
          {
            Header: '料率',
            width: '80',
            accessor: 'monthAverageUnitFee',
            className: 'hidden-sort',
            Cell: ({ row }: Cell<any>) => {
              return (
                <div>
                  {row.original.monthAverageUnitFee
                    ? `${formatDecimal(row.original.monthAverageUnitFee, 2)}%`
                    : ''}
                </div>
              );
            },
          },
          {
            Header: '金額',
            width: '80',
            accessor: 'monthTotalFee',
            className: 'hidden-sort',
            Cell: ({ row }: Cell<any>) => {
              return <div>{formatCurrency(row.original.monthTotalFee || 0)}</div>;
            },
          },
          {
            Header: '原価合計',
            width: '80',
            accessor: 'monthTotalCost',
            className: 'hidden-sort',
            Cell: ({ row }: Cell<any>) => {
              return <div>{formatCurrency(row.original.monthTotalCost || 0)}</div>;
            },
          },
        ],
      },
    ],
    [],
  );

  const singleColumns = useMemo(
    () =>
      [
        {
          name: '販売日',
          width: 100,
          code: 'purchaseDate',
          features: { sortable: true },
          align: 'right',
          render: (value: any) => {
            return (
              <div className={`text-end`}>{value ? dayjs(value).format(DATE_FORMAT_11) : ''}</div>
            );
          },
        },
      ] as AliColumn[],
    [],
  );

  const aliColumns = useMemo(
    () =>
      [
        {
          name: '数量',
          width: 80,
          code: 'amount',
          features: { sortable: false },
          align: 'center',
          render: (value: any) => {
            return <div className={`text-end`}>{value ? formatCurrency(value) : ''}</div>;
          },
        },
        {
          name: '定額料金',
          align: 'center',
          width: 160,
          children: [
            {
              name: '単価',
              width: 80,
              code: 'averageUnitPrice',
              align: 'center',
              render: (value: any) => {
                return <div className={`text-end`}>{formatCurrency(value || 0)}</div>;
              },
            },
            {
              name: '金額',
              width: 80,
              code: 'dayTotalPrice',
              align: 'center',
              render: (value: any) => {
                return <div className={`text-end`}>{formatCurrency(value || 0)}</div>;
              },
            },
          ],
        },
        {
          name: '定率料金',
          align: 'center',
          width: 160,
          children: [
            {
              name: '料率',
              width: 80,
              code: 'averageUnitFee',
              align: 'center',
              render: (value: any) => {
                return (
                  <div className={`text-end`}> {value ? `${formatDecimal(value, 2)}%` : ''}</div>
                );
              },
            },
            {
              name: '金額',
              width: 80,
              code: 'dayTotalFee',
              align: 'center',
              render: (value: any) => {
                return <div className={`text-end`}>{formatCurrency(value || 0)}</div>;
              },
            },
          ],
        },
        {
          name: '原価合計',
          width: 80,
          code: 'dayTotalCost',
          align: 'center',
          render: (value: any) => {
            return <div className={`text-end`}>{formatCurrency(value || 0)}</div>;
          },
        },
      ] as AliColumn[],
    [],
  );

  return (
    <div className="d-flex w-fit">
      <div className="d-flex me-3 mt-105px">
        <AliTable
          wrapperClass="table-group-daily_date w-100px"
          dataSource={dataDays?.map((d: any) => ({ ...d, purchaseDate: d.date })) || []}
          columns={singleColumns}
          onSort={onSort}
          noDataText={null}
        ></AliTable>
      </div>
      {/* duplicate this */}
      {dataSource.map((data) => (
        <div className="d-flex flex-column me-3" key={data.type}>
          <div className="fw-bold">{data.type}</div>
          <TableCommon
            data={[{ ...data }]}
            columns={columns}
            getHeaderProps={() => {
              return {
                className: 'text-white d-flex align-items-center bg-dark text-center',
              };
            }}
            getColumnProps={() => {
              return {
                className: 'text-end',
              };
            }}
            getRowProps={() => {
              return {
                style: { minHeight: '32px' },
              };
            }}
            getCellProps={() => {
              return {
                className: 'bg-white text-dark',
              };
            }}
          />
          <AliTable
            wrapperClass="max-w-480px daily-report-table"
            heightBody="auto"
            dataSource={data?.days}
            columns={aliColumns}
            noDataText={null}
          ></AliTable>
        </div>
      ))}
    </div>
  );
};

export default TableDailyReport;
